import { EventItem, EventType, Tracker, XActionField } from "./interfaces";

interface XPixelEventIds {
  x_page_view_event_id?: string;
  x_content_view_event_id?: string;
  x_add_to_cart_event_id?: string;
  x_lead_event_id?: string;
  x_purchase_event_id?: string;
  x_add_payment_info_event_id?: string;
}

export const getXEventName = (
  event: EventType,
  customEvents?: XPixelEventIds,
): string => {
  // Use custom event names from company settings if available
  if (customEvents) {
    switch (event) {
      case EventType.PAGE_VIEW:
        if (customEvents.x_page_view_event_id)
          return customEvents.x_page_view_event_id;
        break;
      case EventType.VIEW_CONTENT:
      case EventType.view_item_list:
        if (customEvents.x_content_view_event_id)
          return customEvents.x_content_view_event_id;
        break;
      case EventType.ADD_CART:
        if (customEvents.x_add_to_cart_event_id)
          return customEvents.x_add_to_cart_event_id;
        break;
      case EventType.purchase:
        if (customEvents.x_purchase_event_id)
          return customEvents.x_purchase_event_id;
        break;
      case EventType.ADD_BILLING:
        if (customEvents.x_add_payment_info_event_id)
          return customEvents.x_add_payment_info_event_id;
        break;
      case EventType.userLogin:
      case EventType.lead:
      case EventType.contact:
        if (customEvents.x_lead_event_id) return customEvents.x_lead_event_id;
        break;
    }
  }

  // Fallback to default SCREAMING_SNAKE_CASE names
  switch (event) {
    case EventType.ADD_CART:
      return "ADD_TO_CART";
    case EventType.checkout:
      return "CHECKOUT_INITIATED";
    case EventType.purchase:
      return "PURCHASE";
    case EventType.VIEW_CONTENT:
      return "CONTENT_VIEW";
    case EventType.view_item_list:
      return "CONTENT_VIEW";
    case EventType.ADD_BILLING:
      return "ADDED_PAYMENT_INFO";
    case EventType.search:
      return "SEARCH";
    case EventType.add_to_wishlist:
      return "ADD_TO_WISHLIST";
    case EventType.lead:
    case EventType.userLogin:
    case EventType.contact:
      return "LEAD";
    case EventType.complete_registration:
      return "SIGN_UP";
    case EventType.view_cart:
      return "VIEW_CART";
    case EventType.remove_from_cart:
      return "REMOVE_FROM_CART";
    case EventType.add_shipping_info:
      return "ADD_SHIPPING_INFO";

    case EventType.search_event:
      return "SEARCH";
    case EventType.maillist_conversion:
      return "SUBSCRIBE";
    default:
      return event;
  }
};

export const getEventNameForTracker = (
  event: EventType,
  tracker: Tracker,
  customXEvents?: XPixelEventIds,
) => {
  if (tracker === Tracker.X) {
    return getXEventName(event, customXEvents);
  }
  switch (event) {
    case EventType.ADD_CART:
      if (tracker === Tracker.GA || tracker === Tracker.GTAG) {
        return "add_to_cart";
      } else if (tracker === Tracker.FBP || tracker === Tracker.TIK) {
        return "AddToCart";
      }
      return EventType.ADD_CART;

    case EventType.checkout:
      if (tracker === Tracker.GA || tracker === Tracker.GTAG) {
        return "begin_checkout";
      } else if (tracker === Tracker.FBP || tracker === Tracker.TIK) {
        return "InitiateCheckout";
      }
      return EventType.checkout;

    case EventType.purchase:
      if (tracker === Tracker.GA || tracker === Tracker.GTAG) {
        return "purchase";
      } else if (tracker === Tracker.FBP) {
        return "Purchase";
      } else if (tracker === Tracker.TIK) {
        return "CompletePayment";
      }
      return EventType.purchase;

    case EventType.VIEW_CONTENT:
      if (tracker === Tracker.GA || tracker === Tracker.GTAG) {
        return "view_item";
      } else if (tracker === Tracker.FBP || tracker === Tracker.TIK) {
        return "ViewContent";
      }
      return EventType.VIEW_CONTENT;

    case EventType.view_item_list:
      if (tracker === Tracker.GA || tracker === Tracker.GTAG) {
        return "view_item_list";
      } else if (tracker === Tracker.FBP) {
        return "ViewContentList";
      } else if (tracker === Tracker.TIK) {
        // TikTok has no list-view event; ViewContent is its standard equivalent.
        return "ViewContent";
      }
      return EventType.view_item_list;

    case EventType.ADD_BILLING:
      if (tracker === Tracker.GA || tracker === Tracker.GTAG) {
        return "add_payment_info";
      } else if (tracker === Tracker.FBP || tracker === Tracker.TIK) {
        return "AddPaymentInfo";
      }
      return EventType.ADD_BILLING;

    case EventType.view_cart:
      if (tracker === Tracker.TIK) return "ViewContent";
      return event;

    case EventType.search_event:
      if (tracker === Tracker.TIK) return "Search";
      return event;

    case EventType.maillist_conversion:
      if (tracker === Tracker.TIK) return "Subscribe";
      return event;

    // new evwnts for GA and GTM
    case EventType.remove_from_cart:
    case EventType.add_shipping_info:
    case EventType.userLogin:
      return event;

    // added cross-tracker events
    case EventType.search:
      if (tracker === Tracker.GA || tracker === Tracker.GTAG) {
        return "search";
      } else if (tracker === Tracker.FBP || tracker === Tracker.TIK) {
        return "Search";
      }
      return event;
    case EventType.add_to_wishlist:
      if (tracker === Tracker.GA || tracker === Tracker.GTAG) {
        return "add_to_wishlist";
      } else if (tracker === Tracker.FBP || tracker === Tracker.TIK) {
        return "AddToWishlist";
      }
      return event;
    case EventType.lead:
      if (tracker === Tracker.GA || tracker === Tracker.GTAG) {
        return "generate_lead";
      } else if (tracker === Tracker.FBP || tracker === Tracker.TIK) {
        return "Lead";
      }
      return event;
    case EventType.contact:
      if (tracker === Tracker.GA || tracker === Tracker.GTAG) {
        return "contact";
      } else if (tracker === Tracker.FBP || tracker === Tracker.TIK) {
        return "Contact";
      }
      return event;
    case EventType.complete_registration:
      if (tracker === Tracker.GA || tracker === Tracker.GTAG) {
        return "sign_up";
      } else if (tracker === Tracker.FBP || tracker === Tracker.TIK) {
        return "CompleteRegistration";
      }
      return event;
    default:
      return event;
  }
};

export const convertToXFormat = (
  items: any[],
  actionField: any,
  userData?: any,
): XActionField => {
  const params: XActionField = { currency: "", value: 0 };

  if (actionField.currency) params.currency = actionField.currency;
  if (actionField.value !== undefined) params.value = actionField.value;
  if (actionField.search_string)
    params.search_string = actionField.search_string;
  if (actionField.description) params.description = actionField.description;
  if (actionField.status) params.status = actionField.status;

  // Product contents
  if (items && items.length > 0) {
    params.contents = items.map((item) => ({
      content_id:
        item.type === "product"
          ? (item?.others?.parent_id ?? item.inner_id)
          : String(item.inner_id),
      content_name: item?.parent?.name ?? item.name,
      content_price: item.price,
      num_items: item.quantity,
    }));
  }

  // User data for advanced matching
  if (userData?.email) params.email_address = userData.email;
  if (userData?.phone) params.phone_number = userData.phone;

  return params;
};

export const getCartItemFromProductToEvent = (item: EventItem) => {
  return {
    type: item.type,
    inner_id: item.inner_id,
    name: item.name,
    discount: item.discount,
    id: item.id,
    price: item.price,
    quantity: item.quantity,
    others: {
      attrs_values: item.attrs_values,
    },
    parent: item.parent,
    categories_names: item?.categories_names,
  };
};
